/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-15
 */

import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Store as StoreIcon } from '@mui/icons-material';
import { useGetStoresQuery } from '../../../api/shoppingListApi';

interface StoreSelectorProps {
  selectedStoreId: string | null;
  onStoreChange: (storeId: string | null) => void;
  onAddStore: () => void;
}

/**
 * Store selector component for shopping list management.
 * Displays dropdown to select active store and button to add new stores.
 */
const StoreSelector: React.FC<StoreSelectorProps> = ({
  selectedStoreId,
  onStoreChange,
  onAddStore,
}) => {
  const { data: stores, isLoading, error } = useGetStoresQuery();

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={2}
          >
            <CircularProgress size={30} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading stores...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load stores. Please try again.
      </Alert>
    );
  }

  const activeStores = stores?.filter(store => store.is_active) || [];

  return (
    <Card sx={{ mb: { xs: 1.5, sm: 2, md: 3 } }}>
      <CardContent
        sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={{ xs: 1, sm: 2 }}
          flexWrap="wrap"
        >
          <Box
            display="flex"
            alignItems="center"
            gap={{ xs: 0.5, sm: 1 }}
            flex={1}
            minWidth={{ xs: 200, sm: 250 }}
          >
            <StoreIcon
              color="primary"
              sx={{ display: { xs: 'none', sm: 'block' } }}
            />
            <FormControl fullWidth size="small">
              <InputLabel id="store-select-label">Select Store</InputLabel>
              <Select
                labelId="store-select-label"
                id="store-select"
                value={selectedStoreId || ''}
                label="Select Store"
                onChange={e => onStoreChange(e.target.value || null)}
              >
                {activeStores.length === 0 && (
                  <MenuItem disabled>
                    <em>No stores available</em>
                  </MenuItem>
                )}
                {activeStores.map(store => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                    {store.item_count > 0 && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({store.item_count} items)
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Button
            variant="contained"
            startIcon={
              <AddIcon sx={{ display: { xs: 'none', sm: 'inline' } }} />
            }
            onClick={onAddStore}
            size="small"
            sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
          >
            <Box
              component="span"
              sx={{ display: { xs: 'none', sm: 'inline' } }}
            >
              Add Store
            </Box>
            <Box
              component="span"
              sx={{ display: { xs: 'inline', sm: 'none' } }}
            >
              Add
            </Box>
          </Button>
        </Box>

        {activeStores.length === 0 && (
          <Alert
            severity="info"
            sx={{
              mt: { xs: 1, sm: 2 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            No stores found. Create your first store to start managing shopping
            items.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default StoreSelector;
